import { useCallback, useEffect, useState } from 'react';

export enum ESearchDropdownAlignment {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export const useSearchDropdownAlignment = (
  align: ESearchDropdownAlignment,
  isOpened: boolean,
  searchBarPaddingOffset: number,
  triggerButtonRef?: React.RefObject<HTMLDivElement>,
  searchBarRef?: React.RefObject<HTMLDivElement>,
  dropdownRef?: React.RefObject<HTMLDivElement>,
) => {
  const [left, setLeft] = useState<number | undefined>(undefined);
  const [useAlign, setUseAlign] = useState<ESearchDropdownAlignment>(align);
  const [rightAlignConst, setRightAlignConst] = useState<number | undefined>(undefined);
  const [searchBarWidth, setSearchBarWidth] = useState<number | undefined>(undefined);
  const [isPositioned, setIsPositioned] = useState<boolean>(false); // To display the dropdown after getting the correct position

  const calculatePosition = useCallback(() => {
    if (!isOpened) {
      return;
    }

    // Always set positioned to true if we don't have all refs - this prevents blocking the dropdown
    if (!triggerButtonRef?.current || !dropdownRef?.current || !searchBarRef?.current) {
      setIsPositioned(true);
      return;
    }

    const buttonRect = triggerButtonRef.current.getBoundingClientRect();
    const searchBarRect = searchBarRef.current.getBoundingClientRect();
    const currentDropdownWidth = dropdownRef.current.getBoundingClientRect().width;

    const searchBarStart = searchBarRect.left;
    const searchBarWidth = searchBarRect.width;

    const searchBarMaxWidth = searchBarWidth - searchBarPaddingOffset * 2;
    setSearchBarWidth(searchBarMaxWidth);

    const effectiveDropdownWidth = Math.min(currentDropdownWidth, searchBarMaxWidth);
    setRightAlignConst(searchBarWidth - effectiveDropdownWidth - searchBarPaddingOffset);

    let newLeft: number | undefined = undefined;
    let finalAlign = align;

    switch (align) {
      case ESearchDropdownAlignment.Left: {
        // Align dropdown with left of trigger button
        newLeft = buttonRect.left - searchBarStart;
        finalAlign = ESearchDropdownAlignment.Left;
        break;
      }
      case ESearchDropdownAlignment.Center: {
        // Align dropdown with center of trigger button
        const buttonCenter = buttonRect.left + buttonRect.width / 2;
        newLeft = buttonCenter - effectiveDropdownWidth / 2 - searchBarStart;
        finalAlign = ESearchDropdownAlignment.Left;
        break;
      }
      case ESearchDropdownAlignment.Right: {
        // Align dropdown with right of search bar
        finalAlign = ESearchDropdownAlignment.Right;
        break;
      }
      default:
        break;
    }

    // Overflow protection: ensure dropdown stays within search bar bounds
    if (newLeft !== undefined) {
      const dropdownStart = newLeft;
      const dropdownEnd = newLeft + effectiveDropdownWidth;

      // If dropdown would go off the right side, align to right of search bar
      if (dropdownEnd > searchBarWidth - searchBarPaddingOffset) {
        finalAlign = ESearchDropdownAlignment.Right;
        newLeft = undefined;
      }
      // If dropdown would go off the left side, align to left of search bar
      else if (dropdownStart < searchBarPaddingOffset) {
        newLeft = searchBarPaddingOffset;
        finalAlign = ESearchDropdownAlignment.Left;
      }
    }

    setLeft(newLeft);
    setUseAlign(finalAlign);
    setIsPositioned(true);
  }, [align, isOpened, searchBarPaddingOffset, triggerButtonRef, searchBarRef, dropdownRef]);

  useEffect(() => {
    if (isOpened) {
      setIsPositioned(false);
      // Use setTimeout to ensure DOM is updated before calculating position
      const timeoutId = setTimeout(() => {
        calculatePosition();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setIsPositioned(false);
    }
  }, [
    align,
    isOpened,
    searchBarPaddingOffset,
    triggerButtonRef,
    searchBarRef,
    dropdownRef,
    calculatePosition,
  ]);

  // Set up ResizeObserver to watch for dropdown size changes to address loading re-positioning.
  useEffect(() => {
    if (!isOpened || !dropdownRef?.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculatePosition();
    });

    resizeObserver.observe(dropdownRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpened, dropdownRef, calculatePosition]);

  return { left, useAlign, rightAlignConst, searchBarWidth, isPositioned };
};
